import React from 'react';
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Checkbox } from '../../ui/checkbox';
const SendingSection = ({
  formData,
  setFormData,
  errors,
  mailroomConfigs,
  isLoadingConfigs,
  expandedCategories,
  toggleCategoryExpansion,
  handleSendingOptionSelect,
  handleInputChange,
  loadingEmailSubject,
  templateValidationMessage,
  invoiceApproversSection,
  windowLocksSection,
  isDark
}) => {
  // Group configs by grouping_category_id and grouping_category_name
  const groupedConfigs = mailroomConfigs
    ?.filter(config => !config.send_category.includes('***'))
    .reduce((groups, config) => {
      const groupKey = config.grouping_category_id || 'ungrouped';
      const groupName = config.grouping_category_name || 'Ungrouped';
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: groupName,
          position: config.grouping_category_position,
          configs: []
        };
      }
      groups[groupKey].configs.push(config);
      return groups;
    }, {}) || {};

  // Sort groups by position
  const sortedGroupEntries = Object.entries(groupedConfigs)
    .sort(([, groupA], [, groupB]) => {
      if (groupA.position == null && groupB.position == null) return 0;
      if (groupA.position == null) return 1;
      if (groupB.position == null) return -1;
      return groupA.position - groupB.position;
    });

  return (
    <>
      <div className={`box-border content-stretch flex flex-col gap-[16px] items-start p-[20px] relative rounded-[16px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] border-none w-full max-w-[822px] transition-colors duration-200 ${
        isDark ? 'bg-[#000000]' : 'bg-white'
      }`}>
      {/* Title */}
      <p className={`m-0 font-semibold text-[16px] leading-normal relative shrink-0 transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        Sending <span className="text-[red]">*</span>
      </p>

      {/* Loading State */}
      {isLoadingConfigs ? (
        <div className="flex flex-col items-center justify-center py-3 w-full">
          <Loader2 className={`w-5 h-5 animate-spin transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-black'
          }`} />
          <p className={`mt-2 font-normal text-[14px] transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-black'
          }`}>Loading sending options...</p>
        </div>
      ) : mailroomConfigs.length > 0 ? (
        <div className="content-stretch flex flex-col gap-[6px] items-start relative shrink-0 w-full">
          <Accordion type="single" collapsible className="w-full flex flex-col gap-[6px]">
          {sortedGroupEntries.map(([groupKey, group], idx) => {
            const isGroupSelected = group.configs.some(c => c.send_category === formData.sending);
            return (
              <AccordionItem
                key={groupKey}
                value={groupKey}
                className={`group rounded-[8px] overflow-hidden transition-colors duration-200 ${
                  isDark 
                    ? 'data-[state=closed]:bg-[#161515] data-[state=closed]:border-none data-[state=open]:bg-[#000000] data-[state=open]:border data-[state=open]:border-solid data-[state=open]:border-[1px] data-[state=open]:border-[#292929]' 
                    : 'data-[state=closed]:bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa] data-[state=closed]:border-none data-[state=open]:bg-white data-[state=open]:border data-[state=open]:border-solid data-[state=open]:border-[1px] data-[state=open]:border-[#d9d9d9]'
                }`}
              >
                <AccordionTrigger className={`group p-[12px] hover:no-underline border-b border-none m-0 transition-colors duration-200 [&>svg]:transition-colors [&>svg]:duration-200 ${
                  isDark
                    ? isGroupSelected
                      ? 'bg-[#292929] data-[state=open]:bg-[#292929] data-[state=closed]:bg-[#292929] [&>svg]:text-white'
                      : 'bg-[#161515] data-[state=open]:bg-[#292929] data-[state=closed]:bg-[#161515] [&>svg]:text-white'
                    : isGroupSelected
                      ? 'bg-[#fff5eb] data-[state=open]:bg-[#fff5eb] data-[state=closed]:bg-[#fff5eb] [&>svg]:text-[#072837]'
                      : 'bg-[#f5f5f5] data-[state=open]:bg-[#fff5eb] data-[state=closed]:bg-[#f5f5f5] [&>svg]:text-[#072837]'
                }`}>
                  <p className={`font-medium leading-normal text-[16px] m-0 transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-[#072837]'
                  }`}>
                    {group.name}
                  </p>
                </AccordionTrigger>
                <AccordionContent className="m-0 p-0">
                  <div className={`content-stretch flex flex-col gap-[16px] items-start p-[20px] relative shrink-0 w-full transition-colors duration-200 ${
                    isDark ? 'bg-[#000000]' : 'bg-white'
                  }`}>
                      <RadioGroup
                        value={formData.sending}
                        onValueChange={(value) => {
                          const selectedConfig = group.configs.find(c => c.send_category === value);
                          if (selectedConfig) {
                            handleSendingOptionSelect(selectedConfig);
                          }
                        }}
                        className="flex flex-col gap-[16px] w-full"
                      >
                        {group.configs.map((config, idx) => (
                          <div key={config.smata_config_id || config.id} className={`content-stretch flex gap-[8px] items-center relative shrink-0 w-full ${idx > 0 ? '' : ''}`}>
                            {/* Radio Button */}
                            <div className="relative shrink-0 size-[12px] flex items-center justify-center">
                              <RadioGroupItem
                                value={config.send_category}
                                id={`sending-${config.send_category.replace(/\s+/g, '-').toLowerCase()}`}
                              />
                            </div>
                            
                            {/* Template label (left-aligned, no background) */}
                            <div className="flex-1">
                              <p className={`font-normal leading-normal text-[16px] m-0 transition-colors duration-200 ${
                                isDark ? 'text-white' : 'text-[#072837]'
                              }`}>
                                {config.send_category}
                              </p>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>

                      {/* Email Subject Input - Show when selected config has any email template */}
                      {group.configs.some(c => formData.sending === c.send_category && (c.email_template_url || c.agent_email_template_url || c.resident_email_template_url)) && (
                        <div className="mt-[12px] w-full">
                          <div className={`flex items-center border-solid border-[1px] rounded-[8px] overflow-hidden transition-colors duration-200 focus-within:border-[#CD6D2C] ${
                            isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#f5f5f5] border-[#d9d9d9]'
                          }`}>
                            <span className={`px-[16px] py-[12px] text-[16px] font-medium whitespace-nowrap border-r min-w-[160px] text-left transition-colors duration-200 ${
                              isDark
                                ? 'bg-[#292929] text-[#b3b3b3] border-[#292929]'
                                : 'bg-[#f5f5f5] text-[#686868] border-[#d9d9d9]'
                            }`}>
                              Email Subject
                            </span>
                            <input
                              className={`flex-1 px-[16px] py-[12px] text-[16px] border-0 outline-none transition-colors duration-200 ${
                                isDark
                                  ? 'bg-[#000000] text-white placeholder:text-[#b3b3b3]'
                                  : 'bg-white text-[#072837] placeholder:text-[#686868]'
                              }`}
                              type="text"
                              value={formData.emailSubject || ''}
                              placeholder={loadingEmailSubject ? 'Loading...' : ''}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  emailSubject: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      )}

                      {/* Window Locks - shown for any config with window_locks_strata_hub_automation enabled */}
                      {group.configs.some(c => formData.smata_config_id == c.id) && (
                        windowLocksSection
                      )}

                      {/* Invoice Approvers + Special Levies - shown for AGM (6), EGM (7), and Board Meeting Minutes (65) */}
                      {group.configs.some(c => c.id === 6 || c.id === 7 || c.id === 65) && (formData.smata_config_id == 6 || formData.smata_config_id == 7 || formData.smata_config_id == 65) && formData.issueType === 'minutes' && (
                          invoiceApproversSection
                      )}
                      {/* AGM Building Notes Confirmation - shown only when config id 6 is selected */}
                      {group.configs.some(c => c.id === 6) && formData.smata_config_id == 6 && formData.issueType === 'minutes' && (
                        <div className="w-full flex flex-col gap-[20px]">
                          <div className={`flex items-start gap-[12px] p-[16px] rounded-[8px] w-full transition-colors duration-200 ${
                            isDark ? 'bg-[#292929]' : 'bg-[#fff5eb]'
                          }`}>
                            <Checkbox
                              id="agm-building-notes-confirmed"
                              checked={formData.agmBuildingNotesConfirmed || false}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agmBuildingNotesConfirmed: checked }))}
                              isDark={isDark}
                              className="mt-[3px] shrink-0"
                            />
                            <label htmlFor="agm-building-notes-confirmed" className={`text-[14px] leading-[1.5] cursor-pointer transition-colors duration-200 ${
                              isDark ? 'text-white' : 'text-[#072837]'
                            }`}>
                              I confirm Building Notes have been updated as required.{' '}
                              <a
                                href={`https://app.jamesons.com.au/sp-home/${formData.planType}${formData.planNumber}/home`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#CD6D2C] underline font-bold"
                              >
                                Click here to review/update
                              </a>
                            </label>
                          </div>
                          {errors.agmBuildingNotesConfirmed && (
                            <div className="flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
                              <AlertCircle className="w-[16px] h-[16px] flex-shrink-0 mt-[1px]" />
                              <span className="leading-[1.4]">You must confirm building notes have been reviewed</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
          </Accordion>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-3 w-full">
          <p className={`font-normal text-[14px] transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-black'
          }`}>No sending options found</p>
        </div>
      )}

      {/* Other Sending Input */}
      {formData.sending === 'Other' && (
        <div className="w-full">
          <input
            className={`w-full px-[12px] py-[8px] text-[16px] border-none rounded-[8px] outline-none focus:border-[#CD6D2C] focus:border-solid focus:border-[1px] transition-colors duration-200 ${
              isDark
                ? 'bg-[#161515] text-white placeholder:text-[#b3b3b3]'
                : 'bg-[#f5f5f5] text-[#072837] placeholder:text-[#686868]'
            }`}
            type="text"
            placeholder="Enter here"
            value={formData.Other || ''}
            onChange={(e) => handleInputChange('Other', e.target.value)}
          />
        </div>
      )}

      {/* Jotform Section */}
      {/* <div className={`border-none content-stretch flex gap-[16px] items-start p-[16px] relative rounded-[8px] shrink-0 w-full transition-colors duration-200 ${
        isDark ? 'bg-[#161515]' : 'bg-[#f5f5f5] border-solid border-[1px] border-[#f8f9fa]'
      }`}>
        <div className="content-stretch flex flex-col sm:flex-row flex-[1_0_0] gap-3 sm:gap-[24px] items-start sm:items-center min-h-px min-w-px relative shrink-0">
          <p className={`flex-1 font-medium leading-normal min-h-px min-w-px relative text-[14px] sm:text-[16px] m-0 transition-colors duration-200 ${
            isDark ? 'text-white' : 'text-[#072837]'
          }`}>
            Can't find a suitable category? Use Jotform for now
          </p>
          <a
            href="https://form.jotform.com/230221571145040"
            target="_blank"
            rel="noopener noreferrer"
            className={`box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[12px] relative rounded-[12px] shrink-0 no-underline w-full sm:w-auto transition-colors duration-200 ${
              isDark
                ? 'bg-[#cd6d2c] hover:bg-[#ffffff]'
                : 'bg-[#cd6d2c] hover:bg-[#072837]'
            }`}
          >
            <p className={`font-semibold leading-normal relative shrink-0 text-[14px] sm:text-[16px] m-0 transition-colors duration-200 ${
              isDark ? 'text-[#000000]' : 'text-white'
            }`}>
              Go to Jotform
            </p>
            <ExternalLink
              className={`w-[14px] h-[14px] shrink-0 transition-colors duration-200 ${
                isDark ? 'text-[#000000]' : 'text-white'
              }`}
              aria-hidden="true"
            />
          </a>
        </div>
      </div> */}
      

      {/* Error Messages */}
      </div>
      {errors.sending && (
        <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
          <AlertCircle className="w-[16px] h-[16px] flex-shrink-0" />
          <span className="leading-[1.4]">You are required to fill this answer</span>
        </div>
      )}
      {errors.sendingValidation && (
        <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#ff0000]">
          <AlertCircle className="w-[16px] h-[16px] flex-shrink-0" />
          <span className="leading-[1.4]">{templateValidationMessage}</span>
        </div>
      )}
    </>
  );
};

export default SendingSection;

